/**
 * Session bootstrap.
 *
 * `GET /api/v1/me` is the single source for who the user is and what they may
 * do. Applying the tenant theme here means branding is in place before the
 * first screen paints.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { api } from "@/lib/api";
import { applyTenantTheme } from "@/lib/theme";
import type { Me } from "@/lib/types";

/**
 * The signed-in *staff* session.
 *
 * `enabled` exists for the shared form controls, which are rendered on the client
 * portal as well as at the desk. A portal login is refused at this door by design,
 * so a control that asked anyway would fire a 403 per instance on every mount and
 * learn nothing it could act on. Callers that have no use for the answer say so.
 */
export function useMe({ enabled = true }: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<Me>("/v1/me"),
    staleTime: 5 * 60_000,
    retry: 1,
    enabled,
  });

  useEffect(() => {
    if (query.data?.organization.theme) {
      applyTenantTheme(query.data.organization.theme);
    }
  }, [query.data]);

  return query;
}

/**
 * Permission check, against the list the API already resolved.
 *
 * There is deliberately no superuser branch here any more. `permissions` is the
 * *effective* set — the API expands the superuser's break-glass into it — so this
 * asks one question and cannot disagree with the server about the answer.
 *
 * It used to return true for any superuser, which was fine while the flag meant
 * "may do everything". It no longer does: a superuser may administer and read but
 * not register a request or sign a result, and a shortcut here would have put
 * buttons on screen that the API refuses.
 */
export function useCan(me: Me | undefined) {
  return (...codes: string[]): boolean => {
    if (!me) return false;
    return codes.some((code) => me.permissions.includes(code));
  };
}
